import { parseUiLocaleFromSearch } from "./urlLocale";

const STORAGE_KEY = "xpro4_ui_locale";

/** Locale privado BCP47 (ex.: it-x-it1) permitido se a base existir nos idiomas globais da UI. */
export function isWorkspacePrivateLocaleAllowed(code, supportedLngs) {
  if (!code || !Array.isArray(supportedLngs)) return false;
  const s = String(code).trim();
  if (!s) return false;
  if (supportedLngs.includes(s)) return true;
  if (!/-x-/i.test(s)) return false;
  const base = s.split(/-x-/i)[0];
  return supportedLngs.some(
    (c) => !/-x-/i.test(c) && c.toLowerCase() === base.toLowerCase()
  );
}

function readStoredRaw() {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && String(v).trim() ? String(v).trim() : null;
  } catch {
    return null;
  }
}

/**
 * Mapeia idiomas do dispositivo/navegador para um código suportado pela UI.
 * Usa navigator.languages, navigator.language e Intl.DateTimeFormat().resolvedOptions().locale.
 * (Geolocalização por IP não é usada aqui por privacidade e latência.)
 */
export function detectNavigatorUiLocale(supportedLngs) {
  if (typeof window === "undefined") return null;
  if (!Array.isArray(supportedLngs) || !supportedLngs.length) return null;

  const candidates = [];
  try {
    if (Array.isArray(navigator.languages)) {
      for (const x of navigator.languages) {
        if (x) candidates.push(String(x).trim());
      }
    }
    if (navigator.language) candidates.push(String(navigator.language).trim());
  } catch {
    /* ignore */
  }
  try {
    const lo = Intl.DateTimeFormat().resolvedOptions().locale;
    if (lo) candidates.push(String(lo).trim());
  } catch {
    /* ignore */
  }

  const pick = (tag) => {
    if (!tag) return null;
    if (supportedLngs.includes(tag)) return tag;
    const lower = tag.toLowerCase();
    const exactCi = supportedLngs.find((c) => c.toLowerCase() === lower);
    if (exactCi) return exactCi;
    const base = lower.split(/[-_]/)[0];
    const byBase = supportedLngs.find((c) => c.toLowerCase() === base);
    if (byBase) return byBase;
    return (
      supportedLngs.find(
        (c) =>
          lower.startsWith(`${c.toLowerCase()}-`) || lower.startsWith(`${c.toLowerCase()}_`)
      ) || null
    );
  };

  for (const c of candidates) {
    const hit = pick(c);
    if (hit) return hit;
  }
  return null;
}

/**
 * Ordem: query (?lang= / ?locale= / …) → localStorage válido → navegador/Intl → pt-BR (ou primeiro suportado).
 * @returns {{ code: string, shouldPersist: boolean }} shouldPersist=true quando o valor não veio só de um localStorage já válido (inclui detecção automática e fallback inicial).
 */
export function resolveBootstrapUiLanguage(supportedLngs) {
  const fb =
    Array.isArray(supportedLngs) && supportedLngs.includes("pt-BR")
      ? "pt-BR"
      : supportedLngs?.[0] || "pt-BR";

  if (typeof window === "undefined") {
    return { code: fb, shouldPersist: false };
  }

  if (!Array.isArray(supportedLngs) || !supportedLngs.length) {
    return { code: "pt-BR", shouldPersist: false };
  }

  let stored = readStoredRaw();
  if (stored && !isWorkspacePrivateLocaleAllowed(stored, supportedLngs)) {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    stored = null;
  }

  const fromUrl = parseUiLocaleFromSearch(window.location.search);
  if (fromUrl && isWorkspacePrivateLocaleAllowed(fromUrl, supportedLngs)) {
    return { code: fromUrl, shouldPersist: true };
  }

  if (stored && isWorkspacePrivateLocaleAllowed(stored, supportedLngs)) {
    return { code: stored, shouldPersist: false };
  }

  const fromNav = detectNavigatorUiLocale(supportedLngs);
  if (fromNav) {
    return { code: fromNav, shouldPersist: true };
  }

  return { code: fb, shouldPersist: true };
}
